import React, { useState, useEffect } from 'react';
import { useToast } from '@/hooks/useToast';
import { Article } from './ArticlesManagement';
import Modal from '@/components/common/Modal';
import socialShareApi from '@/services/social-share-api';
import { PLATFORM_INFO } from '@/constants/social-platforms';
import { Copy, CheckCircle2 } from 'lucide-react';

interface ShareArticleModalProps {
  isOpen: boolean;
  onClose: () => void;
  article: Article | null;
}

export default function ShareArticleModal({ isOpen, onClose, article }: ShareArticleModalProps) {
  const [step, setStep] = useState(1);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [generatedContent, setGeneratedContent] = useState<Record<string, { title?: string; body: string }>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [direction, setDirection] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const { addToast } = useToast();

  // Reset modal state when it closes
  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setSelectedPlatforms([]);
      setGeneratedContent({});
      setDirection('');
      setCopiedId(null);
    }
  }, [isOpen]);

  if (!article) return null;

  const handlePlatformSelect = (platform: string) => {
    // Only allow one platform to be selected at a time
    setSelectedPlatforms(prev => 
      prev.includes(platform) 
        ? [] // Deselect if clicking the same platform
        : [platform] // Select only this platform
    );
  };

  const handleNext = () => {
    if (selectedPlatforms.length === 1) {
      const platform = selectedPlatforms[0];
      if (platform !== 'custom') {
        setDirection(PLATFORM_INFO[platform as keyof typeof PLATFORM_INFO].direction);
      } else {
        setDirection('Act as a professional content marketer, create a post to share the article');
      }
    } else {
      setDirection('Act as a professional content marketer, create a post to share the article');
    }
    setStep(2);
  };

  const handleGenerate = async () => {
    if (!article) return;
    setIsLoading(true);
    try {
      const contentPromises = selectedPlatforms.map(platform => 
        socialShareApi.generateManualShareContent(article.id, platform.toLowerCase(), direction)
      );
      const results = await Promise.all(contentPromises);
      const newContent = results.reduce((acc, result) => {
        // For email, use subject as title; for reddit, use title
        const title = result.metadata?.subject || result.metadata?.title;
        acc[result.platform] = { 
          title: title, 
          body: result.metadata?.body || result.text 
        };
        return acc;
      }, {} as Record<string, { title?: string; body: string }>);
      setGeneratedContent(newContent);
      setStep(3);
    } catch (error) {
      console.error('Failed to generate content:', error);
      addToast('Failed to generate content. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async (text: string, id: string) => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for dev mode (http) - use textarea trick
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      
      // Show visual feedback
      setCopiedId(id);
      addToast('Copied to clipboard!', 'success');
      
      // Reset feedback after 2 seconds
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      addToast('Failed to copy to clipboard', 'error');
    }
  };

  const renderStepOne = () => (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Select Platforms to Share To</h3>
        <p className="text-sm text-gray-600">Choose one or more platforms where you'd like to share this article</p>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {Object.entries(PLATFORM_INFO).map(([key, { name, icon }]) => (
          <button 
            key={key}
            onClick={() => handlePlatformSelect(key)}
            className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center justify-center hover:shadow-md ${
              selectedPlatforms.includes(key) 
                ? 'border-blue-500 bg-blue-50 shadow-sm' 
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}>
            <div className="w-8 h-8 mb-2">{icon}</div>
            <span className="text-sm font-medium text-gray-900">{name}</span>
          </button>
        ))}
        <button 
          onClick={() => handlePlatformSelect('custom')}
          className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center justify-center hover:shadow-md ${
            selectedPlatforms.includes('custom') 
              ? 'border-blue-500 bg-blue-50 shadow-sm' 
              : 'border-gray-200 bg-white hover:border-gray-300'
          }`}>
          <div className="w-8 h-8 mb-2">✍️</div>
          <span className="text-sm font-medium text-gray-900">Custom</span>
        </button>
      </div>
    </div>
  );

  const renderStepTwo = () => (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Customize Share Direction</h3>
        <p className="text-sm text-gray-600">Provide instructions for how you'd like the content to be generated</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Share Direction</label>
        <textarea 
          value={direction}
          onChange={(e) => setDirection(e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          rows={6}
          placeholder="e.g., Make this post more professional, add emojis, keep it casual..."
        />
      </div>
    </div>
  );

  const renderStepThree = () => (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Copy Your Content</h3>
        <p className="text-sm text-gray-600">Review and copy the generated content for each platform</p>
      </div>
      <div className="space-y-4 max-h-96 overflow-y-auto">
        {selectedPlatforms.map(platform => {
          const content = generatedContent[platform.toLowerCase()];
          const platformInfo = PLATFORM_INFO[platform as keyof typeof PLATFORM_INFO];
          const titleId = `${platform}-title`;
          const bodyId = `${platform}-body`;
          const isEmail = platform.toLowerCase() === 'email';
          const titleLabel = isEmail ? 'Subject' : 'Title';
          
          return (
            <div key={platform} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-b border-gray-200 flex items-center gap-3">
                <div className="w-6 h-6">{platformInfo?.icon}</div>
                <h4 className="font-semibold text-gray-900">{platformInfo?.name || platform}</h4>
              </div>
              <div className="p-4 space-y-3">
                {content?.title && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">{titleLabel}</label>
                    <div className="flex items-start gap-2">
                      <div className="flex-grow bg-gray-50 border border-gray-200 rounded p-3 text-sm text-gray-700 break-words">
                        {content.title}
                      </div>
                      <button 
                        onClick={() => handleCopy(content.title!, titleId)}
                        className={`flex-shrink-0 p-2 rounded transition-all ${
                          copiedId === titleId
                            ? 'bg-green-100 text-green-700'
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                        }`}
                        title="Copy title">
                        {copiedId === titleId ? (
                          <CheckCircle2 size={18} />
                        ) : (
                          <Copy size={18} />
                        )}
                      </button>
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Body</label>
                  <div className="flex items-start gap-2">
                    <div className="flex-grow bg-gray-50 border border-gray-200 rounded p-3 text-sm text-gray-700 whitespace-pre-wrap break-words max-h-32 overflow-y-auto">
                      {content?.body}
                    </div>
                    <button 
                      onClick={() => handleCopy(content?.body || '', bodyId)}
                      className={`flex-shrink-0 p-2 rounded transition-all ${
                        copiedId === bodyId
                          ? 'bg-green-100 text-green-700'
                          : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                      }`}
                      title="Copy body">
                      {copiedId === bodyId ? (
                        <CheckCircle2 size={18} />
                      ) : (
                        <Copy size={18} />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Share: ${article.title}`}>
      <div className="p-6">
        {isLoading ? (
          <div className="flex justify-center items-center h-48">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
              <p className="text-gray-600">Generating content...</p>
            </div>
          </div>
        ) : (
          <>
            {step === 1 && renderStepOne()}
            {step === 2 && renderStepTwo()}
            {step === 3 && renderStepThree()}
          </>
        )}
        <div className="mt-8 flex justify-between items-center border-t border-gray-200 pt-6">
          <div>
            {step > 1 && (
              <button 
                onClick={() => setStep(step - 1)} 
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium">
                Back
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button 
              onClick={onClose} 
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium">
              Cancel
            </button>
            <button 
              onClick={step === 1 ? handleNext : step === 2 ? handleGenerate : onClose}
              disabled={step === 1 && selectedPlatforms.length === 0}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium">
              {step === 3 ? 'Close' : step === 2 ? 'Generate' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
